<template>
  <section class="exam-faq" aria-label="Exam system frequently asked questions">
    <ol class="exam-faq-list">
      <li>
        <h3>What should I do if the system incorrectly flags me for talking, leaving the camera, network disconnection, or screen switching?</h3>
        <p>
          Violations related to talking, leaving the camera, network disconnection, and screen switches are automatically detected and penalized by the system — these decisions cannot be reversed. Your best preparation is to familiarize yourself with the system and rules through the mock test and ensure your own compliance. As long as you can continue answering, focus on your exam. <strong>Do not send messages</strong> to explain or appeal these violations — related messages will be filtered by the system.
        </p>
      </li>

      <li>
        <h3>What is the difference between a violation and cheating?</h3>
        <p>
          Violations are automatically detected by the system. When total deductions reach 20 points, the exam environment is deemed non-compliant and the score is marked invalid. Cheating is determined manually: the system flags abnormal cases, which are then reviewed by humans using multi-dimensional evidence. If confirmed as cheating, the score is canceled.
        </p>
      </li>

      <li>
        <h3>What actions are considered screen switching?</h3>
        <p>
          System pop-ups, minimizing the browser, clicking outside the browser window, using translation tools, engaging with chat software, opening browsers other than Google Chrome, and running antivirus software (e.g., Security Guard) are all detected as screen switches.
        </p>
      </li>

      <li>
        <h3>Will screen switch counts affect my score?</h3>
        <p>
          No — screen switch counts do not directly affect your score. Abnormal screen switches are only used as one piece of evidence, alongside other evidence, in manual reviews to determine whether cheating has occurred. <strong>As long as you are not actually cheating, you can safely ignore the screen switch count.</strong>
        </p>
      </li>

      <li>
        <h3>How is “talking” detected during the exam?</h3>
        <p>
          Continuous sound lasting over 1.5 seconds counts as talking (brief noises like a single cough do not). Deductions apply: 1 point for every 5 seconds accumulated, and 1 point even for a single incident under 5 seconds. <strong>You are responsible for maintaining a quiet exam environment.</strong>
        </p>
      </li>

      <li>
        <h3>Am I allowed to leave my seat (restroom, water, stationery, etc.)?</h3>
        <p>
          No. Leaving your seat during the exam is not allowed. Deductions apply when your face leaves the camera area — 2 points per incident. <strong>Prepare everything you need before the exam starts.</strong>
        </p>
      </li>

      <li>
        <h3>What happens if my network disconnects during the exam?</h3>
        <p>
          You will be unable to continue answering. To prevent cheating via intentional disconnection, the system deducts 1 point per network interruption. <strong>You are responsible for maintaining a stable connection.</strong>
        </p>
      </li>

      <li>
        <h3>What are the consequences of violation deductions?</h3>
        <p>
          Deductions are applied directly to your final score. If total deductions from talking, leaving the camera, or network disconnections reach 20 points, the exam environment is deemed non-compliant and your score will be marked invalid.
        </p>
      </li>

      <li>
        <h3>Why is the mouse cursor hidden?</h3>
        <p>The cursor is hidden by design to prevent the use of translation plugins.</p>
      </li>

      <li>
        <h3>What should I do if I need to use the restroom?</h3>
        <p>
          Send a Message to report it, stating your departure time (HH:MM) and expected return time (HH:MM). Note that reporting does not grant exemption — leaving the camera area will still result in point deductions.
        </p>
      </li>

      <li>
        <h3>Can I submit the exam early?</h3>
        <p>Yes, you may submit early after the first 30 minutes.</p>
      </li>

      <li>
        <h3>Can I refresh the page if it goes blank or stalls?</h3>
        <p>Yes, but check your network connection first.</p>
      </li>

      <li>
        <h3>What if I close the page by mistake?</h3>
        <p>
          You can re-enter from the home page as long as you haven't missed the entry window. Your answers are saved in real time when the network is stable. Please verify your answer status after re-entry.
        </p>
      </li>
    </ol>
  </section>
</template>

<script>
export default {
  name: "QA",
};
</script>

<style scoped>
.exam-faq {
  color: #34425d;
  font-family: Montserrat-Regular, sans-serif;
  text-align: left;
}

.exam-faq-list {
  margin: 0;
  padding-left: 1.75rem;
}

.exam-faq-list li {
  padding: 0 0 1.1rem 0.25rem;
}

.exam-faq-list li + li {
  padding-top: 1.1rem;
  border-top: 1px solid #e1e6f0;
}

.exam-faq-list li::marker {
  color: #4468ec;
  font-family: Montserrat-SemiBold, sans-serif;
  font-weight: 700;
}

.exam-faq-list h3 {
  margin: 0;
  color: #193563;
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.45;
}

.exam-faq-list p {
  margin: 0.45rem 0 0;
  color: #5e687b;
  font-size: 0.8125rem;
  line-height: 1.65;
}

.exam-faq-list strong {
  color: #263f6d;
  font-family: Montserrat-SemiBold, sans-serif;
  font-weight: 700;
}

@media (max-width: 640px) {
  .exam-faq-list {
    padding-left: 1.4rem;
  }

  .exam-faq-list h3 {
    font-size: 0.875rem;
  }

  .exam-faq-list p {
    font-size: 0.78125rem;
  }
}
</style>
